import { useMutation, useQuery } from '@tanstack/react-query';
import {
  connexion,
  motDePasseOublie,
  poseMotDePasseInitial,
  presenterUnAcces,
  sessionCourante,
} from '@groupe-trousseau/shared/api-client';
import { deballer, lireLErreur } from '../../shared/api/client';

/**
 * **La connexion — `CDC-1` F-05, servie par la page 6 de `CDC-6`.**
 *
 * **LE CONTENU DE CET ÉCRAN NE SE REJOUE PAS ICI.** `CDC-6` §2 est explicite : « **le
 * contenu de l'écran de connexion**, tranché en `CDC-1` et non rejoué — ce document décide
 * **où cet écran vit, jamais ce qu'il porte** ». Ce hook n'ajoute donc aucune règle : il
 * appelle la route, et c'est le serveur qui décide.
 *
 * **CE QUI SE PASSE À LA RÉUSSITE N'EST PAS UNE REDIRECTION AVEC UNE VALEUR.** F-71 : « à
 * travers cette page, ce qui est retenu est une **DESTINATION, JAMAIS UNE VALEUR QUI VAUT
 * ACCÈS**. Sans cette borne, la page de connexion deviendrait un endroit où une valeur
 * d'accès attend, **sur le domaine public**. » La session est posée par le serveur dans un
 * cookie que ce code ne voit pas ; **la page ne transporte rien**, elle change d'adresse.
 */
export function useConnexion() {
  return useMutation({
    mutationFn: (identifiants: { email: string; motDePasse: string }) =>
      deballer(connexion({ body: identifiants })),
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA SESSION VIVANTE — F-71 CRITÈRE 3, ET ELLE NE SE LIT QUE SUR CETTE PAGE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-71 : « **elle reconnaît une session vivante et emmène la personne dans son espace sans
 * redemander son mot de passe.** » Critère 3 : « QUAND une session vivante est présente, LE
 * SYSTÈME DOIT emmener la personne dans son espace **sans redemander de mot de passe**. »
 *
 * **ET C'EST LA SEULE PAGE DU SITE QUI REGARDE**, ce que la même règle impose par l'autre
 * bout : « **les sept autres pages ne savent rien de personne** : elles montrent la même
 * chose à tout le monde, et le chemin dit toujours "se connecter" ». Un cadre commun qui
 * lirait la session pour adapter son menu aurait fait des huit pages des pages
 * personnalisées, donc non indexables à l'identique (F-73).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **UN `401` N'EST PAS UNE ERREUR ICI, C'EST LA RÉPONSE ATTENDUE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Sur cette page, **la plupart des visiteurs n'ont pas de session** : c'est le cas nominal.
 * L'appel échoue donc en `401`, et la page doit alors **afficher le formulaire**, jamais une
 * erreur. C'est l'inverse de `web/`, où un `401` signifie qu'une session est **tombée** — et
 * c'est pourquoi `shared/api/client.ts` de ce dossier « ne traite pas de `401` : il n'y a
 * aucune donnée protégée à afficher ici ».
 *
 * **`retry: false` est donc une exigence et non un réglage** : trois tentatives sur un refus
 * attendu retarderaient l'affichage du formulaire de trois allers-retours, sur la page par
 * laquelle tout le produit s'ouvre.
 *
 * **Et rien n'est mis en cache** — `staleTime: 0`, `gcTime: 0` : une session lue puis gardée
 * ferait entrer, au second passage sur cette page, quelqu'un qui vient de se déconnecter
 * ailleurs. F-71 veut qu'on se déconnecte **vers cette page** ; elle doit donc constater
 * l'absence de session à chaque fois qu'elle s'affiche.
 */
export function useSessionVivante() {
  return useQuery({
    queryKey: ['session-vivante'],
    queryFn: () => deballer(sessionCourante()),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **« MOT DE PASSE OUBLIÉ » VIT SUR CETTE PAGE, ET IL N'Y A PAS DE NEUVIÈME PAGE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **C'EST UN DÉFAUT TROUVÉ EN OUVRANT LE LOT-6, ET IL VAUT D'ÊTRE ÉCRIT.** Au LOT-4b, la
 * page de connexion portait un lien `<a href="/mot-de-passe-oublie">` — donc vers une
 * adresse qui **ne correspond à aucune des huit pages**, et qui tombait sur la page de
 * refus. Deux règles se rencontraient sans que rien ne le signale :
 *
 * - **la liste des huit est fermée** (F-69), donc une page « mot de passe oublié » ne peut
 *   pas exister ;
 * - **le code n'est pas un lien** (F-71) : « c'est une valeur que la personne **saisit là où
 *   elle l'a demandée, sur cette page** ».
 *
 * La seconde règle donne la forme de la première : **il n'y a pas de page à créer**, parce
 * que tout se passe ici. Le lien était donc l'erreur, et non l'absence de la page.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **TROIS ROUTES POUR UN SEUL GESTE, ET AUCUNE N'EST NOUVELLE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Le chemin existe entièrement depuis le LOT-1, et le LOT-6 n'ajoute **aucune** route :
 *
 * 1. `POST /api/v1/auth/mot-de-passe-oublie` — demande le code. « **Mécanisme unique** : il
 *    sert l'activation d'un compte comme la réinitialisation. **La réponse est identique que
 *    l'adresse ait un compte ou non.** »
 * 2. `POST /api/v1/acces/:valeur` — présente le code avec l'adresse, et rend **un billet**.
 * 3. `POST /api/v1/auth/mot-de-passe/initial` — pose le nouveau mot de passe avec le billet,
 *    **et ouvre la session**.
 *
 * **LE BILLET N'EST PAS UNE VALEUR QUI VAUT ACCÈS, ET C'EST CE QUI REND CE CHEMIN CONFORME À
 * F-71.** « À travers cette page, ce qui est retenu est une **DESTINATION, JAMAIS UNE VALEUR
 * QUI VAUT ACCÈS**. » Un billet ne donne accès à rien : il n'ouvre aucune session, il ne lit
 * aucune donnée, et il ne sert qu'à la route de pose — `access.controller.ts` l'écrit,
 * « **le billet n'est pas une valeur d'accès** — il ne vaut que pour la route de pose ». Il
 * vit dix minutes, un seul usage, et **il ne quitte jamais la mémoire de l'onglet** : ni
 * adresse, ni stockage, ni cookie.
 */

/** Étape 1 — demander le code. **La réponse ne dit pas si le compte existe.** */
export function useDemandeDeCode() {
  return useMutation({
    mutationFn: (email: string) =>
      deballer(motDePasseOublie({ body: { email } })),
  });
}

/**
 * **Étapes 2 et 3 en une seule mutation, et le regroupement est un choix qui s'explique.**
 *
 * Les deux appels sont **indissociables du point de vue de la personne** : elle saisit un
 * code et un nouveau mot de passe, et elle entre. Les séparer en deux mutations exposerait
 * un état intermédiaire — « billet obtenu, mot de passe pas encore posé » — qu'aucune règle
 * ne demande d'afficher, et **qui obligerait à garder le billet dans un état de composant
 * entre deux gestes**. Ici il ne sort pas de cette fonction.
 *
 * **La suite `session-ouverte` est traitée, et elle n'est pas théorique** : un compte qui a
 * déjà un mot de passe et qui présente un code valide reçoit directement une session. La
 * route de pose n'est alors **pas appelée**, et il n'y a rien à poser.
 *
 * **La suite `adresse-changee` n'est PAS traitée ici**, et ce n'est pas un oubli : elle
 * répond à un code de changement d'adresse, demandé depuis les réglages de l'application,
 * pas depuis cette page. Elle lève, et le formulaire le dit.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE RÔLE SE LIT APRÈS COUP, ET AUCUNE DES DEUX ROUTES NE LE REND — C'EST UN CONSTAT
 * DE CONTRAT, PAS UN DÉTOUR.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `POST /api/v1/auth/connexion` rend `role`, et c'est ce qui décide où part la personne.
 * **Ni `POST /api/v1/acces/:valeur`, ni `POST /api/v1/auth/mot-de-passe/initial` ne le
 * rendent** : la première rend `{ suite, billet, session }` où la session est réduite à son
 * instant d'expiration, la seconde rend `{ expireLe, compteActif }`.
 *
 * **Ce chemin lit donc la session une fois la session ouverte**, par la route qui existe
 * pour cela. C'est un appel de plus, et il vaut mieux que les deux autres issues : ajouter
 * `role` à deux réponses de `CDC-1` **modifierait un contrat que ce lot n'a pas à toucher**
 * — `CDC-6` §2 pose que « le contenu de l'écran de connexion est tranché en `CDC-1` et non
 * rejoué » —, et deviner la destination sans le rôle enverrait un `orgadmin` dans
 * l'application de séance.
 */
export function usePoseDeMotDePasse() {
  return useMutation({
    mutationFn: async (saisie: {
      email: string;
      code: string;
      motDePasse: string;
    }) => {
      const presente = await deballer(
        presenterUnAcces({
          path: { valeur: saisie.code },
          body: { email: saisie.email },
        }),
      );

      if (presente.suite === 'poser-un-mot-de-passe') {
        if (presente.billet === null) {
          throw new Error(
            'Le serveur n’a pas rendu de billet de pose. Demandez un nouveau code.',
          );
        }
        await deballer(
          poseMotDePasseInitial({
            body: { billet: presente.billet, motDePasse: saisie.motDePasse },
          }),
        );
      } else if (presente.suite !== 'session-ouverte') {
        // **Un refus nommé plutôt qu'un état muet.** Une suite inattendue sur cette page
        // veut dire que le code présenté n'était pas un code de connexion — un code de
        // changement d'adresse, par exemple. Le dire vaut mieux que de rester sur un
        // formulaire qui semble n'avoir rien fait.
        throw new Error(
          'Ce code ne sert pas à se connecter. Demandez-en un nouveau depuis cette page.',
        );
      }

      // La session est ouverte dans les deux branches : c'est ici qu'on apprend le rôle.
      const compte = await deballer(sessionCourante());
      return { role: compte.compte.role };
    },
  });
}

/**
 * **Où va une personne qui vient de se connecter, SELON SON RÔLE.**
 *
 * « **Un seul écran de connexion pour les quatre rôles** » (Q169, Q528) : « il n'y a donc
 * qu'une route de connexion, et **aucune adresse cachée, aucun écran d'administration
 * séparé**. Une seconde route réservée à l'administration aurait suffi à trahir quelles
 * adresses en sont. »
 *
 * **La destination, elle, diffère**, et cela ne trahit rien : elle se décide **après** que
 * le serveur a répondu, donc pour une personne qui est déjà entrée. Un `orgadmin` et le
 * compte qui voit tout vont à la surface d'administration ; un tuteur et un élève vont à
 * l'application de séance.
 */
export function destinationApres(role: string | null): string {
  const admin = import.meta.env.VITE_ADMIN_URL ?? '';
  const app = import.meta.env.VITE_APP_URL ?? '';
  const vaEnAdministration = role === 'ORGADMIN' || role === 'SUPERADMIN';
  const cible = vaEnAdministration ? admin : app;
  return cible === '' ? '/' : cible;
}

export { lireLErreur };
